import { useMemo, useState, type ClipboardEvent, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import {
  FieldError,
  Group,
  Input,
  Label,
  Tag as RACTag,
  TagGroup,
  TagList,
  Text,
  TextField as TextFieldBase,
  type TextFieldProps as TextFieldBaseProps,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { FaLock, FaX } from "react-icons/fa6";
import { css, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { formField, type FormFieldVariant } from "../../../../styled-system/recipes";
import { tagChipStyles } from "../styles/styles";
import { Button } from "../../core/basic/Button";
import { defaultTagColor } from "../../core/basic/Tag";
import { CustomTriggerTooltip } from "../../core/basic/Tooltip";
import BulkEditModal from "../../core/basic/BulkEditModal";

export interface StringListInputProps extends Omit<
  TextFieldBaseProps,
  "value" | "defaultValue" | "onChange" | "children"
> {
  value: string[];
  onChange: (value: string[]) => void;

  label?: ReactNode;
  description?: string;
  errorMessage?: string;
  placeholder?: string;

  variant?: FormFieldVariant["variant"];
  css?: SystemStyleObject;
  startIcon?: ReactNode;

  /** Show "Bulk edit" button that opens a textarea modal. Defaults to `true`. */
  bulkEdit?: boolean;
  /** Title shown in the bulk edit modal. */
  bulkEditTitle?: string;
  /** Separators used when splitting pasted/bulk text into items. Default `/[\n,]/`. */
  parseSeparators?: RegExp;

  /** Transform applied to every new item, e.g. `(s) => s.trim().toUpperCase()`. */
  normalizer?: (value: string) => string;
  /** Returns an error message or `null` when valid. Invalid items are dropped with a field-level error. */
  validator?: (value: string) => string | null;

  /** Optional chip row rendered above the input; clicking a suggestion adds it. */
  suggestions?: string[];

  /**
   * Values that may not be removed, e.g. because they are already persisted server side. They render
   * with a lock instead of a remove button, and survive bulk edit. New values can still be added.
   */
  lockedValues?: string[];
  /** Explains why locked values cannot be removed. Shown when hovering or focusing a locked chip. */
  lockedValueHint?: string;
}

const chipRowStyles = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "xsmall",
});

const suggestionsRowStyles = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "xsmall",
  alignItems: "center",
  fontSize: "small",
  color: "text.secondary",
});

const suggestionChipButton = css.raw({
  cursor: "pointer",
  borderRadius: "15px",
  paddingY: "xxsmall",
  paddingX: "small",
  fontSize: "small",
  fontWeight: 600,
  color: "white",
  backgroundColor: "var(--tag-color)",
  border: "none",
  _hover: { opacity: 0.9 },
});

const labelRowStyles = css({
  display: "flex",
  gap: "xsmall",
  alignItems: "center",
  flexWrap: "wrap",
});

const bulkEditButtonStyles = css({
  marginLeft: "auto",
});

const lockIconStyles = css({
  display: "inline-flex",
  fontSize: "xxsmall",
  cursor: "help",
});

function dedupeMerge(existing: string[], incoming: string[]): string[] {
  const seen = new Set(existing);
  const out = [...existing];
  for (const item of incoming) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

export function StringListInput({
  value,
  onChange,
  label,
  description,
  errorMessage,
  placeholder,
  variant,
  css: cssProp,
  startIcon,
  bulkEdit = true,
  bulkEditTitle,
  parseSeparators = /[\n,]/,
  normalizer,
  validator,
  suggestions,
  lockedValues,
  lockedValueHint,
  isInvalid,
  ...rest
}: StringListInputProps) {
  const { t } = useTranslation(["common"]);
  const { isDisabled, isReadOnly } = rest;
  const classes = formField({ variant });
  const [inputValue, setInputValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);

  const filteredSuggestions = useMemo(
    () => (suggestions ?? []).filter((s) => s && !value.includes(s)),
    [suggestions, value],
  );

  const locked = useMemo(() => new Set(lockedValues), [lockedValues]);

  const processRaw = (raw: string[]): { accepted: string[]; firstError: string | null } => {
    const accepted: string[] = [];
    let firstError: string | null = null;
    for (const rawItem of raw) {
      const normalized = normalizer ? normalizer(rawItem) : rawItem.trim();
      if (!normalized) continue;
      if (validator) {
        const err = validator(normalized);
        if (err) {
          if (!firstError) firstError = err;
          continue;
        }
      }
      accepted.push(normalized);
    }
    return { accepted, firstError };
  };

  const addFromText = (text: string) => {
    const pieces = text.split(parseSeparators);
    const { accepted, firstError } = processRaw(pieces);
    setValidationError(firstError);
    if (accepted.length) {
      onChange(dedupeMerge(value, accepted));
    }
  };

  const commitInput = () => {
    if (!inputValue) return;
    addFromText(inputValue);
    setInputValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && !inputValue && value.length) {
      // Backspace removes the most recent addition, stepping over locked values rather than
      // swallowing the keystroke when the last chip happens to be locked.
      let last = value.length - 1;
      while (last >= 0 && locked.has(value[last]!)) last--;
      if (last < 0) return;
      e.preventDefault();
      onChange(value.filter((_, i) => i !== last));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!parseSeparators.test(pasted)) return;
    e.preventDefault();
    addFromText(inputValue + pasted);
    setInputValue("");
  };

  const handleBlur = () => {
    if (inputValue) commitInput();
  };

  const addSuggestion = (s: string) => {
    if (isDisabled || isReadOnly) return;
    onChange(dedupeMerge(value, [s]));
  };

  const combinedError = errorMessage ?? validationError ?? undefined;
  const canEdit = !isDisabled && !isReadOnly;
  const canBulkEdit = bulkEdit && canEdit;

  return (
    <TextFieldBase
      {...rest}
      value={inputValue}
      onChange={setInputValue}
      isInvalid={isInvalid ?? !!combinedError}
      className={cx(classes.fieldWrapper, css(cssProp))}
    >
      {(label || canBulkEdit) && (
        <div className={labelRowStyles}>
          {label && <Label className={classes.label}>{label}</Label>}
          {canBulkEdit && (
            <Button variant="link" className={bulkEditButtonStyles} onPress={() => setIsBulkOpen(true)}>
              {t(($) => $.common["Bulk edit"])}
            </Button>
          )}
        </div>
      )}
      {filteredSuggestions.length > 0 && canEdit && (
        <div className={suggestionsRowStyles}>
          <span>{t(($) => $.common["Suggested:"])}</span>
          {filteredSuggestions.map((s) => (
            <Button
              key={s}
              variant="contentOnly"
              css={suggestionChipButton}
              style={{ "--tag-color": defaultTagColor } as React.CSSProperties}
              onPress={() => addSuggestion(s)}
            >
              + {s}
            </Button>
          ))}
        </div>
      )}
      <Group
        className={cx(
          classes.inputBox,
          css({
            flexWrap: "wrap",
            gap: "xsmall",
            paddingY: "xsmall",
            borderRadius: "card",
          }),
        )}
      >
        {startIcon && <span className={css({ fontSize: "medium", fontWeight: "300" })}>{startIcon}</span>}
        {value.length > 0 && (
          <TagGroup
            aria-label={t(($) => $.common["Selected values"])}
            onRemove={
              canEdit ?
                (keys) => {
                  const removed = new Set(Array.from(keys, String).filter((k) => !locked.has(k)));
                  if (!removed.size) return;
                  onChange(value.filter((v) => !removed.has(v)));
                }
              : undefined
            }
          >
            <TagList items={value.map((v) => ({ id: v }))} className={chipRowStyles}>
              {(item) => (
                <RACTag
                  id={item.id}
                  textValue={item.id}
                  className={tagChipStyles}
                  style={{ "--tag-color": defaultTagColor } as CSSProperties}
                >
                  {item.id}
                  {canEdit &&
                    (locked.has(item.id) ?
                      <CustomTriggerTooltip toolTip={lockedValueHint}>
                        <span role="img" aria-label={lockedValueHint} className={lockIconStyles}>
                          <FaLock />
                        </span>
                      </CustomTriggerTooltip>
                    : <Button variant="contentOnly" slot="remove">
                        <FaX className={css({ fontSize: "xxsmall", fontWeight: "bold" })} />
                      </Button>)}
                </RACTag>
              )}
            </TagList>
          </TagGroup>
        )}
        <Input
          className={cx(classes.input, css({ minWidth: "80px" }))}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
        />
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {combinedError && <FieldError className={classes.error}>{combinedError}</FieldError>}
      {bulkEdit && (
        <BulkEditModal
          isOpen={isBulkOpen}
          onClose={() => setIsBulkOpen(false)}
          title={bulkEditTitle}
          parseSeparators={parseSeparators}
          defaultValues={value}
          onSave={(raw) => {
            const { accepted, firstError } = processRaw(raw);
            setValidationError(firstError);
            // Locked values keep their place in the list even if the user deleted them in the textarea.
            onChange(
              dedupeMerge(
                value.filter((v) => locked.has(v)),
                accepted,
              ),
            );
          }}
        />
      )}
    </TextFieldBase>
  );
}
